import React, { useState } from "react";
import { saveApiKey, loadApiKey, clearApiKey } from "@engine/llm/apiKeyStore.js";
import { ClaudeClient } from "@engine/llm/ClaudeClient.js";
import { NullLLMClient, setLLMClient, getLLMClient } from "@engine/llm/LLMClient.js";
import { audio, type VolumeChannel } from "@engine/audio/AudioManager.js";
import { applyRetro, isRetroEnabled } from "@engine/renderer/RetroPresentation.js";

interface Props {
  onBack: () => void;
}

/**
 * Settings panel for user-supplied Anthropic API keys (L02).
 * Rendered as a sub-view inside MainMenu.
 *
 * Saving a non-empty key:
 *   1. Persists to localStorage via apiKeyStore.
 *   2. Swaps the active LLMClient to a ClaudeClient with the new key.
 *
 * Clearing the key:
 *   1. Removes from localStorage.
 *   2. Resets to NullLLMClient.
 */
export function SettingsUI({ onBack }: Props) {
  const [keyInput, setKeyInput] = useState(() => loadApiKey() ?? "");
  const [showKey, setShowKey] = useState(false);
  const [saved, setSaved] = useState(false);

  const isCurrentlyConfigured = getLLMClient().isConfigured;

  function handleSave() {
    const trimmed = keyInput.trim();
    if (!trimmed) return;
    saveApiKey(trimmed);
    setLLMClient(new ClaudeClient(trimmed));
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function handleClear() {
    clearApiKey();
    setLLMClient(new NullLLMClient());
    setKeyInput("");
    setSaved(false);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
      <h3
        style={{
          color: "white",
          fontFamily: "monospace",
          margin: 0,
          fontSize: 14,
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        API Settings
      </h3>

      <StatusBadge configured={isCurrentlyConfigured} />

      <label style={{ color: "#aaa", fontFamily: "monospace", fontSize: 12 }}>
        Anthropic API Key
      </label>

      <div style={{ display: "flex", gap: 6 }}>
        <input
          type={showKey ? "text" : "password"}
          value={keyInput}
          onChange={(e) => { setKeyInput(e.target.value); setSaved(false); }}
          placeholder="sk-ant-…"
          spellCheck={false}
          autoComplete="off"
          style={{
            flex: 1,
            background: "#111",
            color: "#eee",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 4,
            padding: "6px 10px",
            fontFamily: "monospace",
            fontSize: 12,
            outline: "none",
          }}
        />
        <button
          onClick={() => setShowKey((s) => !s)}
          title={showKey ? "Hide key" : "Show key"}
          style={iconBtnStyle}
        >
          {showKey ? "▲" : "▼"}
        </button>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <SettingsButton
          label={saved ? "Saved!" : "Save"}
          onClick={handleSave}
          disabled={!keyInput.trim()}
          primary
        />
        <SettingsButton label="Clear" onClick={handleClear} disabled={!keyInput && !isCurrentlyConfigured} />
      </div>

      <p style={{ color: "#666", fontFamily: "monospace", fontSize: 11, margin: 0 }}>
        Your key is stored only in this browser's localStorage and is never
        sent anywhere except Anthropic's API.
      </p>

      <h3 style={sectionHeaderStyle}>Sound</h3>
      <VolumeSlider channel="master" label="Master" />
      <VolumeSlider channel="sfx" label="Effects" />
      <VolumeSlider channel="ambient" label="Ambience" />

      <h3 style={sectionHeaderStyle}>Presentation</h3>
      <RetroToggle />

      <SettingsButton label="← Back" onClick={onBack} />
    </div>
  );
}

function VolumeSlider({ channel, label }: { channel: VolumeChannel; label: string }) {
  const [value, setValue] = useState(() => audio.getVolume(channel));
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: "#aaa",
        fontFamily: "monospace",
        fontSize: 12,
      }}
    >
      <span style={{ width: 72 }}>{label}</span>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => {
          const v = Number.parseFloat(e.target.value);
          setValue(v);
          audio.setVolume(channel, v);
        }}
        style={{ flex: 1 }}
      />
      <span style={{ width: 32, textAlign: "right" }}>{Math.round(value * 100)}%</span>
    </label>
  );
}

function RetroToggle() {
  const [on, setOn] = useState(() => isRetroEnabled());
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        color: "#aaa",
        fontFamily: "monospace",
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => {
          setOn(e.target.checked);
          applyRetro(e.target.checked);
        }}
      />
      Retro display (chunky pixels + scanlines)
    </label>
  );
}

const sectionHeaderStyle: React.CSSProperties = {
  color: "white",
  fontFamily: "monospace",
  margin: "10px 0 0",
  fontSize: 14,
  letterSpacing: 1,
  textTransform: "uppercase",
};

function StatusBadge({ configured }: { configured: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontFamily: "monospace",
        fontSize: 12,
        color: configured ? "#4caf50" : "#888",
      }}
    >
      <span style={{ fontSize: 10 }}>{configured ? "●" : "○"}</span>
      {configured ? "API key configured — LLM features active" : "No API key — LLM features unavailable"}
    </div>
  );
}

function SettingsButton({
  label,
  onClick,
  disabled,
  primary,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: primary ? 1 : undefined,
        background: disabled ? "#222" : primary ? "#2a5a2a" : "#333",
        color: disabled ? "#555" : "white",
        border: "1px solid",
        borderColor: disabled ? "#333" : primary ? "#4caf50" : "rgba(255,255,255,0.3)",
        borderRadius: 4,
        padding: "7px 16px",
        fontFamily: "monospace",
        fontSize: 12,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {label}
    </button>
  );
}

const iconBtnStyle: React.CSSProperties = {
  background: "#222",
  color: "#aaa",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: 4,
  padding: "0 8px",
  fontFamily: "monospace",
  fontSize: 10,
  cursor: "pointer",
};
